import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Webcam from "react-webcam";

const API_BASE = "https://ims-backend.indevconsultancy.in";
const API_BASE_API = "https://api-ims.indevconsultancy.in";

const Requisition = () => {
  // State hooks
  const [requisitionList, setRequisitionList] = useState([]);
  const [stockOptions, setStockOptions] = useState([]);
  const [stockCategory, setStockCategory] = useState([]);
  const [stockVendor, setVendor] = useState([]);
  const [kitchenId, setKitchenId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterDate, setFilterDate] = useState("");
  const [filterRequisitionDate, setRequisitionDate] = useState("");
  const [formErrors, setFormErrors] = useState([]);
  const [capturedImages, setCapturedImages] = useState({});
  const [webcamRow, setWebcamRow] = useState(null);
  const [currentFacingMode, setCurrentFacingMode] = useState("user");
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedRequisitionStatus, setSelectedRequisitionStatus] = useState(null);
  const [REID, setSelectedREID] = useState("");
  const [REID1, setSelectedREID1] = useState("");

  // Price update mode states
  const [isPriceUpdateMode, setIsPriceUpdateMode] = useState(false);
  const [updatableItems, setUpdatableItems] = useState([]);

  // Received items states
  const [receivedItems, setReceivedItems] = useState([]);
  const [receivedQuantities, setReceivedQuantities] = useState({});
  const [receivedPrices, setReceivedPrices] = useState({});
  const [receivedTotals, setReceivedTotals] = useState({});
  const [receivedInvoices, setReceivedInvoices] = useState({});

  // Pending items states
  const [getPandingValue, setPandingValue] = useState([]);
  const [getPandingValueUpdate, setPandingValueUpdate] = useState([]);
  const [getPandingDataShow, setPandingDataShow] = useState([]);
  const [formErrorPanding, setFormErrorPanding] = useState("");
  const [capturedImagesPanding, setCapturedImagesPanding] = useState({});
  const [webcamRowPanding, setWebcamRowPanding] = useState(null);
  const [currentFacingModePanding, setCurrentFacingModePanding] = useState("user");

  // Form input groups - initialize with current date
  const [inputGroups, setInputGroups] = useState(() => {
    const today = new Date().toISOString().split("T")[0];
    return [{
      item: "",
      available: "",
      quantity: "",
      date: today,
      category_item: "",
      vendor_id: "",
    }];
  });

  // Item details state
  const [itemDetails, setItemDetails] = useState([]);

  // Refs
  const webcamRefs = useRef({});
  const webcamRefsPanding = useRef({});

  // Constants
  const ITEMS_PER_PAGE = 10;
  const STATUS_MAP = {
    0: { label: "Request", color: "secondary" },
    1: { label: "Approved", color: "warning" },
    2: { label: "Received", color: "success" },
    3: { label: "Pending", color: "warning" },
    4: { label: "Delete", color: "danger" }
  };

  // Memoized values
  const today = useMemo(() => new Date().toISOString().split("T")[0], []);
  const paginatedList = useMemo(() =>
    requisitionList.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE),
    [requisitionList, currentPage]
  );
  const totalPages = useMemo(() =>
    Math.ceil(requisitionList.length / ITEMS_PER_PAGE),
    [requisitionList.length]
  );
  const grandTotal = useMemo(() =>
    Object.values(receivedTotals).reduce((sum, total) => sum + (total || 0), 0),
    [receivedTotals]
  );

  // Utility functions
  const getEmptyInputGroup = useCallback(() => {
    return {
      item: "",
      available: "",
      quantity: "",
      date: today,
      category_item: "",
      vendor_id: "",
    };
  }, [today]);

  const fetchData = useCallback(async (url, setter, errorMsg) => {
    try {
      const res = await axios.get(url);
      setter(res.data);
    } catch (err) {
      console.error(errorMsg, err);
    }
  }, []);

  // Load user once
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user?.kitchen) {
      setKitchenId(user.kitchen);
    }
  }, []);

  // Run API when kitchenId is updated
  useEffect(() => {
    if (kitchenId) {
      fetchRequisitionList();
    }
  }, [kitchenId]);

  // Reset price update mode when modal closes
  useEffect(() => {
    const modalElement = document.getElementById('viweModal');
    if (modalElement) {
      const handleHidden = () => {
        setIsPriceUpdateMode(false);
        setUpdatableItems([]);
      };
      modalElement.addEventListener('hidden.bs.modal', handleHidden);
      return () => {
        modalElement.removeEventListener('hidden.bs.modal', handleHidden);
      };
    }
  }, []);

  useEffect(() => {
    fetchData(
      `${API_BASE}/admin-panel/category/`,
      setStockCategory,
      "Error fetching categories:"
    );
    fetchData(
      `${API_BASE}/admin-panel/vendor/`,
      setVendor,
      "Error fetching vendors:"
    );
  }, [fetchData]);

  const stocksItem = useCallback(async (categoryId, index) => {
    if (!kitchenId) return;
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await axios.get(
        `${API_BASE}/api/itemlist/?categoryid=${categoryId}&kitchenid=${user.kitchen}`
      );
      setStockOptions(prev => {
        const updated = [...prev];
        updated[index] = res.data;
        return updated;
      });
    } catch (err) {
      console.error("Error fetching stock options:", err);
    }
  }, [kitchenId]);

  // Fetch requisition list
  const fetchRequisitionList = useCallback((date = "", status = "") => {
    if (!kitchenId) return;

    let url = `${API_BASE}/api/requistion-list/?kitchen_id=${kitchenId}`;
    const params = [];
    if (date) params.push(`date=${encodeURIComponent(date)}`);
    if (status) params.push(`status=${encodeURIComponent(status)}`);
    if (params.length) url += `&${params.join("&")}`;

    axios.get(url)
      .then(response => {
        if (response.data.requisitiondata) {
          const filtered = response.data.requisitiondata.filter(item => item.status !== 4);
          setRequisitionList(filtered);
          setCurrentPage(1);
        }
      })
      .catch(error => console.error("Failed to fetch requisition list:", error));
  }, [kitchenId]);

  // Fetch item details
  const fetchRequisitionItemDetails = async (pk, status = null) => {
    try {
      const response = await axios.get(
        `${API_BASE}/api/item-request-list/${pk}/`
      );
      const items = response.data.requisitiondataitem || [];
      setItemDetails(items);
      setSelectedRequisitionStatus(status);

      // Initialize received items data only if status is not "Request" (0)
      if (status !== 0 && status !== "0") {
        const initialQuantities = {};
        const initialPrices = {};
        const initialTotals = {};
        const initialInvoices = {};

        items.forEach(item => {
          initialQuantities[item.id] = parseFloat(item.recieved_quantity) || 0;
          initialPrices[item.id] = parseFloat(item.price) || 0;
          initialTotals[item.id] = (parseFloat(item.recieved_quantity) || 0) * (parseFloat(item.price) || 0);
          initialInvoices[item.id] = item.invoice_number || "";
        });

        setReceivedQuantities(initialQuantities);
        setReceivedPrices(initialPrices);
        setReceivedTotals(initialTotals);
        setReceivedInvoices(initialInvoices);
        setReceivedItems(items);
      } else {
        // For Request status, just set the items without received data
        setReceivedItems(items);
      }
    } catch (error) {
      console.error("Error fetching item details:", error);
    }
  };

  // Form handlers
  const handleInputChange = (index, field, value, availableQty = "") => {
    setInputGroups(prev => prev.map((group, i) =>
      i === index ? {
        ...group,
        [field]: value,
        ...(field === "item" && availableQty ? { available: availableQty } : {})
      } : group
    ));
  };

  const addInputGroup = () => {
    setInputGroups(prev => [...prev, getEmptyInputGroup()]);
  };

  const removeInputGroup = (index) => {
    setInputGroups(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = inputGroups.reduce((acc, group, idx) => {
      if (!group.date) acc.push(`Row ${idx + 1}: Please select Date of Received.`);
      if (!group.item) acc.push(`Row ${idx + 1}: Item name is required.`);
      if (!group.quantity) acc.push(`Row ${idx + 1}: Quantity must be greater than 0.`);
      if (!group.category_item) acc.push(`Row ${idx + 1}: Category is required.`);
      return acc;
    }, []);

    if (errors.length) {
      setFormErrors(errors);
      return;
    }

    setFormErrors([]);

    try {
      const payload = {
        items: inputGroups.map(group => ({
          item: group.item,
          quantity: group.quantity,
          kitchenId: kitchenId,
          available: group.available || "",
          date: group.date,
          category: group.category_item,
          vendor_id: group.vendor_id || 0,
        }))
      };

      const response = await axios.post(`${API_BASE}/api/add-request-items`, payload);

      if (response.status === 200 || response.status === 201) {
        setInputGroups([getEmptyInputGroup()]);
        fetchRequisitionList();
        document.getElementById("registerModal")?.querySelector('[data-bs-dismiss="modal"]')?.click();
        alert("Requisition added successfully!");
      }
    } catch (error) {
      console.error("API error:", error);
      alert("An error occurred while saving data");
    }
  };

  // Received items handlers
  const handleReceivedQuantityChange = (itemId, value) => {
    const quantity = parseFloat(value) || 0;
    const price = receivedPrices[itemId] || 0;

    setReceivedQuantities(prev => ({
      ...prev,
      [itemId]: quantity
    }));

    setReceivedTotals(prev => ({
      ...prev,
      [itemId]: quantity * price
    }));
  };

  const handleReceivedPriceChange = (itemId, value) => {
    const price = parseFloat(value) || 0;
    const quantity = receivedQuantities[itemId] || 0;

    setReceivedPrices(prev => ({
      ...prev,
      [itemId]: price
    }));

    setReceivedTotals(prev => ({
      ...prev,
      [itemId]: quantity * price
    }));
  };

  const handleReceivedInvoiceChange = (itemId, value) => {
    setReceivedInvoices(prev => ({
      ...prev,
      [itemId]: value
    }));
  };

  const submitReceivedItems = async () => {
    try {
      const payload = receivedItems.map(item => ({
        id: item.id,
        itemid: item.id,
        kitchen: item.kitchen,
        reqid: item.reqid,
        stock: item.stock,
        stock_id: item.stock_id,
        category_id: item.category_id,
        category_name: item.category_name,
        requestquantity: item.requestquantity,
        approve_date: item.approve_date,
        recievedquantity: item.recievedquantity,
        required_date: item.required_date,
        remarks: item.remarks,
        vendor_id: item.vendor_id,
        vendor_name: item.vendor_name,
        recieved_quantity: receivedQuantities[item.id] || 0,
        approve_quantity: item.approve_quantity,
        pendingquantity: item.pendingquantity,
        recieve_date: item.recieve_date,
        totalquantiy: item.totalquantiy,
        kitchenid: kitchenId,
        recivedquantity: receivedQuantities[item.id] || 0,
        price: receivedPrices[item.id] || 0,
        amount: receivedTotals[item.id] || 0,
        invoice_number: receivedInvoices[item.id] || "",
        imageurl: capturedImages[item.id] || ""
      }));

      console.log("Submitting received items:", payload);

      const response = await axios.post(
        `${API_BASE}/api/recivequantity/`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        // Optional: Log to PHP API
        try {
          await axios.post(`${API_BASE_API}/recive-log-riqu.php`, payload, {
            headers: {
              "Content-Type": "application/json",
            },
          });
        } catch (logError) {
          console.warn("Logging failed, but main operation succeeded:", logError);
        }

        setWebcamRow(null);
        setCapturedImages({});
        setCurrentFacingMode("user");
        setReceivedQuantities({});
        setReceivedPrices({});
        setReceivedTotals({});
        setReceivedInvoices({});
        fetchRequisitionList();
        alert("Received items updated successfully!");
        document.getElementById('viweModal')?.querySelector('[data-bs-dismiss="modal"]')?.click();
      }
    } catch (error) {
      console.error("Error submitting received items:", error);
      alert("Failed to submit received items. Please try again.");
    }
  };

  // Price update function
  const submitPriceUpdate = async () => {
    try {
      // Filter only items that have been received
      const itemsToUpdate = receivedItems.filter(item =>
        item.recieved_quantity &&
        item.recieved_quantity !== "" &&
        item.recieved_quantity !== "0" &&
        parseFloat(item.recieved_quantity) > 0
      );

      const payload = itemsToUpdate.map(item => ({
        id: item.id,
        itemid: item.id,
        kitchen: item.kitchen,
        reqid: item.reqid,
        stock: item.stock,
        stock_id: item.stock_id,
        category_id: item.category_id,
        category_name: item.category_name,
        requestquantity: item.requestquantity,
        approve_date: item.approve_date,
        recievedquantity: item.recievedquantity,
        required_date: item.required_date,
        remarks: item.remarks,
        vendor_id: item.vendor_id,
        vendor_name: item.vendor_name,
        recieved_quantity: item.recieved_quantity, // Keep original quantity
        approve_quantity: item.approve_quantity,
        pendingquantity: item.pendingquantity,
        recieve_date: item.recieve_date,
        totalquantiy: item.totalquantiy,
        kitchenid: kitchenId,
        recivedquantity: item.recieved_quantity, // Keep original quantity
        price: receivedPrices[item.id] || item.price || 0, // Updated price
        amount: (parseFloat(item.recieved_quantity) || 0) * (parseFloat(receivedPrices[item.id] || item.price || 0)), // Recalculate amount
        invoice_number: receivedInvoices[item.id] ?? item.invoice_number ?? "",
        imageurl: item.imageurl || "" // Keep original image
      }));

      console.log("Submitting price update:", payload);

      const response = await axios.post(
        `${API_BASE}/api/recivequantity/`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        // Optional: Log to PHP API
        try {
          await axios.post(`${API_BASE_API}/price-update-log.php`, payload, {
            headers: {
              "Content-Type": "application/json",
            },
          });
        } catch (logError) {
          console.warn("Logging failed, but main operation succeeded:", logError);
        }

        setIsPriceUpdateMode(false);
        setUpdatableItems([]);
        fetchRequisitionList();
        alert("Prices updated successfully!");

        // Close the modal
        document.getElementById('viweModal')?.querySelector('[data-bs-dismiss="modal"]')?.click();
      }
    } catch (error) {
      console.error("Error updating prices:", error);
      alert("Failed to update prices. Please try again.");
    }
  };

  // Image capture handlers
  const captureImage = (itemId) => {
    const webcamInstance = webcamRefs.current[itemId];
    const imageSrc = webcamInstance?.getScreenshot();
    if (!imageSrc) return;

    setCapturedImages(prev => ({ ...prev, [itemId]: imageSrc }));
    setWebcamRow(null);
  };

  const captureImagePanding = (itemId) => {
    const webcamInstance = webcamRefsPanding.current[itemId];
    const imageSrc = webcamInstance?.getScreenshot();
    if (!imageSrc) return;

    setCapturedImagesPanding(prev => ({ ...prev, [itemId]: imageSrc }));
    setWebcamRowPanding(null);
  };

  const removeImage = (itemId) => {
    setCapturedImages(prev => {
      const updated = { ...prev };
      delete updated[itemId];
      return updated;
    });
  };

  const removeImagePanding = (itemId) => {
    setCapturedImagesPanding(prev => {
      const updated = { ...prev };
      delete updated[itemId];
      return updated;
    });
  };

  // Pending items handlers
  const pandingValueRecevi = async (pk, status = null) => {
    try {
      const response = await axios.get(
        `${API_BASE}/api/item-request-list/${pk}/`
      );
      const items = response.data.requisitiondataitem || [];
      setPandingValue(items);
      setPandingValueUpdate(items);
      setSelectedRequisitionStatus(status);

      // Initialize pending received data
      const initialQuantities = {};
      const initialPrices = {};

      items.forEach(item => {
        initialQuantities[item.id] = parseFloat(item.recieved_quantity) || 0;
        initialPrices[item.id] = parseFloat(item.unit_price) || 0;
      });

      setReceivedQuantities(prev => ({ ...prev, ...initialQuantities }));
      setReceivedPrices(prev => ({ ...prev, ...initialPrices }));
    } catch (error) {
      console.error("Error fetching item details:", error);
    }
  };

  const handleQtyChange = (index, itemId, value) => {
    const quantity = parseFloat(value) || 0;
    const price = receivedPrices[itemId] || 0;

    setReceivedQuantities(prev => ({
      ...prev,
      [itemId]: quantity
    }));

    setPandingValueUpdate(prev =>
      prev.map((item, i) =>
        i === index ? { ...item, recieved_quantity: quantity } : item
      )
    );
  };

  const submitPandingValue = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user?.kitchen) {
      setFormErrorPanding("Kitchen ID not found.");
      return;
    }

    try {
      const payload = getPandingValueUpdate
        .filter(item => (item.recieved_quantity || 0) > 0)
        .map(item => {
          const quantity = Number(receivedQuantities[item.id] || 0);
          const price = Number(receivedPrices[item.id] || 0);

          return {
            pendingquantity: quantity,
            stock_id: item.stock_id,
            item_request_id: item.id,
            kitchen_id: user.kitchen,
            vendor_id: item.vendor_id || 0,
            unit_price: price,
            total_amount: quantity * price,
            image: capturedImagesPanding[item.id] || "",
          };
        });

      if (payload.length === 0) {
        setFormErrorPanding("Please enter received quantities greater than 0.");
        return;
      }

      console.log("Submitting pending items:", payload);

      const response = await axios.post(
        `${API_BASE}/api/pendingquantity/`,
        payload
      );

      if (response.status === 200) {
        fetchRequisitionList();
        setWebcamRowPanding(null);
        setCapturedImagesPanding({});
        setCurrentFacingModePanding("user");
        setFormErrorPanding("");
        alert("Pending items submitted successfully!");
        document.getElementById('editModal')?.querySelector('[data-bs-dismiss="modal"]')?.click();
      }
    } catch (error) {
      console.error("Submission failed:", error);
      setFormErrorPanding("Failed to submit. Please try again.");
    }
  };

  const getPanding = async (pk) => {
    try {
      const response = await axios.get(
        `${API_BASE}/api/pendingquantity/?requistion_id=${pk}`
      );
      setPandingDataShow(response.data || []);
    } catch (error) {
      console.error("Error fetching pending details:", error);
    }
  };

  // Pagination
  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToNext = () => goToPage(currentPage + 1);
  const goToPrev = () => goToPage(currentPage - 1);

  // Render helpers
  const renderStatusBadge = (status) => (
    <span className={`badge bg-${STATUS_MAP[status]?.color || "dark"}`}>
      {STATUS_MAP[status]?.label || "Unknown"}
    </span>
  );

  const renderWebcam = (itemId, isOpen, onClose, facingMode, onSwitchCamera, onCapture, capturedImage, onRemove) => {
    if (capturedImage) {
      return (
        <div>
          <img
            src={capturedImage}
            alt="Captured"
            className="img-thumbnail mb-2"
            style={{ width: "80px", height: "60px", objectFit: "cover" }}
          />
          <button className="btn btn-danger btn-sm w-100" onClick={onRemove}>
            Remove Image
          </button>
        </div>
      );
    }

    if (!isOpen) {
      return (
        <button className="btn btn-outline-primary btn-sm" onClick={() => onClose(itemId)}>
          <i className="bi bi-camera"></i> Capture
        </button>
      );
    }

    return (
      <div className="d-flex flex-column align-items-center gap-2">
        <Webcam
          audio={false}
          ref={el => webcamRefs.current[itemId] = el}
          screenshotFormat="image/jpeg"
          videoConstraints={{ facingMode }}
          className="rounded border"
          style={{ width: "150px", height: "100px", objectFit: "cover" }}
        />
        <div className="d-flex gap-2">
          <button className="btn btn-success btn-sm" onClick={onCapture}>
            Capture
          </button>
          <button className="btn btn-info btn-sm" onClick={onSwitchCamera}>
            Switch ({facingMode === "user" ? "Front" : "Back"})
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => onClose(null)}>
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // Check if status is "Request" (0)
  const isRequestStatus = selectedRequisitionStatus === 0 || selectedRequisitionStatus === "0";

  // Check if status is "Pending" (3)
  const isPendingStatus = selectedRequisitionStatus === 3 || selectedRequisitionStatus === "3";

  return (
    <div className="container-fluid px-2">
      <div className="content">
        <div className="container-fluid">
          {/* Header */}
          <div className="row align-items-center justify-content-between py-2">
            <div className="col-md-6 col-6">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb justify-content-start mb-0">
                  <li className="breadcrumb-item"><Link to="/mobilehome">Home</Link></li>
                </ol>
              </nav>
            </div>
            <div className="col-md-6 col-6 text-end">
              <span className="fs-1">Requisition List</span>
            </div>
          </div>

          {/* Main Card */}
          <div className="row g-3">
            <div className="col-md-12">
              <div className="card">
                {/* Filters */}
                <div className="card-header border-info">
                  <form onSubmit={(e) => { e.preventDefault(); fetchRequisitionList(filterRequisitionDate, filterDate); }}>
                    <div className="row g-3">
                      <div className="col-lg">
                        <select className="form-control" value={filterDate} onChange={(e) => setFilterDate(e.target.value)}>
                          <option value="">Select Status</option>
                          {Object.entries(STATUS_MAP).map(([value, { label }]) =>
                            <option key={value} value={value}>{label}</option>
                          )}
                        </select>
                      </div>
                      <div className="col-lg">
                        <input type="date" className="form-control" value={filterRequisitionDate} onChange={(e) => setRequisitionDate(e.target.value)} />
                      </div>
                      <div className="col-lg-2">
                        <button type="submit" className="btn btn-primary w-100">Search</button>
                      </div>
                      <div className="col-lg-1">
                        <button type="button" className="btn btn-outline-dark w-100" onClick={() => { setFilterDate(""); setRequisitionDate(""); fetchRequisitionList(); }}>
                          Reset
                        </button>
                      </div>
                    </div>
                  </form>
                </div>

                {/* Table */}
                <div className="card-body">
                  <div className="row g-0 align-items-center">
                    <div className="col-sm-7 mb-3">
                      <h6 className="fw-bold mb-0">Total Requisition(s): <span>{requisitionList.length}</span></h6>
                    </div>
                    <div className="col-sm-5 mb-3 text-end">
                      <button className="btn btn-primary add-item" data-bs-toggle="modal" data-bs-target="#registerModal">
                        Add <i className="fas fa-plus"></i>
                      </button>
                    </div>
                  </div>

                  <div className="table-responsive scrollbar">
                    <table className="table text-center table-bordered table-striped fs--1 mb-0">
                      <thead className="bg-primary text-white">
                        <tr>
                          <th>S.No.</th>
                          <th>Requisition ID</th>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedList.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="text-center">No requisitions found</td>
                          </tr>
                        ) : (
                          paginatedList.map((item, index) => (
                            <tr key={item.pk}>
                              <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                              <td>RE00{item.reqid}</td>
                              <td>{new Date(item.requestdate).toLocaleDateString()}</td>
                              <td>{renderStatusBadge(item.status)}</td>
                              <td>
                                <div className="d-flex gap-2 justify-content-center">
                                  <button className="btn btn-primary btn-sm" data-bs-toggle="modal" data-bs-target="#viweModal"
                                    onClick={() => {
                                      setSelectedItemId(item.reqid);
                                      fetchRequisitionItemDetails(item.reqid, item.status);
                                      setIsPriceUpdateMode(false);
                                      setUpdatableItems([]);
                                    }}>
                                    View <i className="fas fa-eye"></i>
                                  </button>

                                  <button className="btn btn-secondary btn-sm" data-bs-toggle="modal" data-bs-target="#showPandingData"
                                    onClick={() => {
                                      setSelectedItemId(item.reqid);
                                      getPanding(item.reqid);
                                    }}>
                                    Pending <i className="fas fa-eye"></i>
                                  </button>

                                  {/* Show "Pending Received" button only for status 3 (Pending) */}
                                  {item.status == 3 && (
                                    <button className="btn btn-warning btn-sm" data-bs-toggle="modal" data-bs-target="#editModal"
                                      onClick={() => {
                                        setSelectedREID("RE00" + item.reqid);
                                        setSelectedREID1(item.reqid);
                                        setSelectedItemId(item.reqid);
                                        pandingValueRecevi(item.reqid, item.status);
                                      }}>
                                      Pending Received
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {requisitionList.length > ITEMS_PER_PAGE && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <button className="btn btn-outline-primary btn-sm" onClick={goToPrev} disabled={currentPage === 1}>
                        Previous
                      </button>
                      <span>Page {currentPage} of {totalPages}</span>
                      <button className="btn btn-outline-primary btn-sm" onClick={goToNext} disabled={currentPage === totalPages}>
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========== ADD REQUISITION MODAL ========== */}
      <div className="modal fade" id="registerModal" tabIndex="-1">
        <div className="modal-dialog modal-fullscreen">
          <div className="modal-content">
            <form onSubmit={handleSubmit}>
              <div className="modal-header">
                <h5 className="modal-title">Add Requisition Item</h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" />
              </div>
              <div className="modal-body">
                {formErrors.length > 0 && (
                  <div className="alert alert-danger">
                    <ul>{formErrors.map((msg, i) => <li key={i}>{msg}</li>)}</ul>
                  </div>
                )}
                {inputGroups.map((group, index) => (
                  <div className="row g-2 mb-3" key={index}>
                    <div className="col-12 col-md-2">
                      <select className="form-control" value={group.category_item} onChange={(e) => {
                        handleInputChange(index, "category_item", e.target.value);
                        stocksItem(e.target.value, index);
                      }}>
                        <option value="">Select Category</option>
                        {stockCategory.map(category => (
                          <option key={category.category_id} value={category.category_id}>
                            {category.category_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12 col-md-2">
                      <select className="form-control" value={group.item} onChange={(e) => {
                        const selectedOption = e.target.selectedOptions[0];
                        const availableQty = selectedOption.getAttribute('data-availablequantity');
                        handleInputChange(index, "item", e.target.value, availableQty);
                      }}>
                        <option value="">Select Item</option>
                        {(stockOptions[index] || []).map(item => (
                          <option key={item.stock_id} value={item.stock_id} data-availablequantity={item.total_quantity}>
                            {item.stock_name} - Available: {item.total_quantity}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6 col-md-2">
                      <input type="number" className="form-control" placeholder="Available" value={group.available || ""} readOnly />
                    </div>
                    <div className="col-6 col-md-2">
                      <input type="number" className="form-control" placeholder="Quantity" value={group.quantity}
                        onChange={(e) => handleInputChange(index, "quantity", e.target.value)} />
                    </div>
                    <div className="col-6 col-md-2">
                      <input type="date" className="form-control" value={group.date}
                        onChange={(e) => handleInputChange(index, "date", e.target.value)} />
                    </div>
                    <div className="col-12 col-md-2">
                      {index === 0 ? (
                        <button type="button" className="btn btn-success w-100" onClick={addInputGroup}>
                          <i className="fas fa-plus"></i> Add
                        </button>
                      ) : (
                        <button type="button" className="btn btn-danger w-100" onClick={() => removeInputGroup(index)}>
                          <i className="fas fa-minus"></i> Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                <button type="submit" className="btn btn-primary">Submit</button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* ========== EDIT MODAL (PENDING RECEIVED) ========== */}
      <div className="modal fade" id="editModal" tabIndex="-1">
        <div className="modal-dialog modal-fullscreen">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Pending Quantity Received - {REID}</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" />
            </div>
            <div className="modal-body">
              {formErrorPanding && (
                <div className="alert alert-danger">{formErrorPanding}</div>
              )}

              {getPandingValue.length === 0 ? (
                <p className="text-center">No pending items available</p>
              ) : (
                <div className="table-responsive">
                  <table className="table text-center table-bordered table-striped fs--1 mb-0">
                    <thead className="bg-primary text-white">
                      <tr>
                        <th>Item</th>
                        <th>Pending Qty</th>
                        <th>Received Qty</th>
                        <th>Unit Price</th>
                        <th>Total Amount</th>
                        <th>Image</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPandingValue.map((item, index) =>
                        item.pendingquantity > 0 ? (
                          <tr key={item.id}>
                            <td>{item.stock}</td>
                            <td>{item.pendingquantity}</td>
                            <td>
                              <input
                                type="number"
                                className="form-control"
                                value={receivedQuantities[item.id] || ""}
                                onChange={(e) => handleQtyChange(index, item.id, e.target.value)}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                value={receivedPrices[item.id] || ""}
                                onChange={(e) => handleReceivedPriceChange(item.id, e.target.value)}
                              />
                            </td>
                            <td className="fw-bold">
                              ₹{((receivedQuantities[item.id] || 0) * (receivedPrices[item.id] || 0)).toFixed(2)}
                            </td>
                            <td>
                              {renderWebcam(
                                item.id,
                                webcamRowPanding === item.id,
                                setWebcamRowPanding,
                                currentFacingModePanding,
                                () => setCurrentFacingModePanding(prev => prev === "user" ? "environment" : "user"),
                                () => captureImagePanding(item.id),
                                capturedImagesPanding[item.id],
                                () => removeImagePanding(item.id)
                              )}
                            </td>
                          </tr>
                        ) : null
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="table-success">
                        <td colSpan="4" className="text-end fw-bold">Total Pending Received:</td>
                        <td colSpan="2" className="text-start fw-bold">
                          ₹{getPandingValue.reduce((sum, item) => {
                            const qty = receivedQuantities[item.id] || 0;
                            const price = receivedPrices[item.id] || 0;
                            return sum + (qty * price);
                          }, 0).toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
              <button type="button" className="btn btn-primary" onClick={submitPandingValue}>Submit</button>
            </div>
          </div>
        </div>
      </div>

      {/* ========== VIEW MODAL (RECEIVE ITEMS) ========== */}
      <div className="modal fade" id="viweModal" tabIndex="-1">
        <div className="modal-dialog modal-fullscreen">
          <div className="modal-content">
            <div className="modal-header">
              <div className="d-flex justify-content-between align-items-center w-100">
                <div>
                  <h5 className="modal-title mb-0">
                    {isPriceUpdateMode ? "Update Prices - " : (isRequestStatus ? "View Request - " : "Receive Items - ")}
                    RE00{selectedItemId}
                    <span className={`badge bg-${STATUS_MAP[selectedRequisitionStatus]?.color || "dark"} ms-2`}>
                      {STATUS_MAP[selectedRequisitionStatus]?.label || "Unknown"}
                    </span>
                  </h5>
                  {isPriceUpdateMode && (
                    <small className="text-warning">You can only update prices for already received items</small>
                  )}
                </div>

                {/* Price Update Button - Show only for Received status (2) */}
                {/* {selectedRequisitionStatus == 2 && !isRequestStatus && (
                  <button 
                    type="button" 
                    className={`btn ${isPriceUpdateMode ? 'btn-warning' : 'btn-outline-warning'} btn-sm`}
                    onClick={() => {
                      if (!isPriceUpdateMode) {
                        // Enter price update mode
                        setIsPriceUpdateMode(true);
                        setUpdatableItems(receivedItems.filter(item => 
                          item.recieved_quantity && 
                          item.recieved_quantity !== "" && 
                          item.recieved_quantity !== "0" &&
                          parseFloat(item.recieved_quantity) > 0
                        ));
                      } else {
                        // Exit price update mode
                        setIsPriceUpdateMode(false);
                        setUpdatableItems([]);
                      }
                    }}
                  >
                    {isPriceUpdateMode ? 'Cancel Price Update' : 'Update Prices'}
                  </button>
                )} */}
              </div>
              <button type="button" className="btn-close" data-bs-dismiss="modal" />
            </div>
            <div className={`modal-body py-2 ${isPriceUpdateMode ? 'price-update-mode' : ''}`}>
              {receivedItems.length === 0 ? (
                <p className="text-center text-danger">No items found</p>
              ) : (
                <div className="table-responsive">
                  <table className="table text-center table-bordered table-striped fs--1 mb-0">
                    <thead className="bg-primary text-white">
                      <tr>
                        <th>S.No.</th>
                        <th>Category Name</th>
                        <th>Item</th>
                        <th>Requested Qty</th>
                        <th>Requested Date</th>
                        <th>Vendor Name</th>
                        <th>Approved</th>
                        <th>Approve Date</th>

                        {!isRequestStatus && (
                          <>
                            <th>Received Qty</th>
                            <th>Unit Price</th>
                            <th>Invoice No.</th>
                            <th>Total Amount</th>
                            <th>Image</th>
                          </>
                        )}
                        {isRequestStatus && (
                          <th>Required Date</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {receivedItems.map((item, index) => (
                        <tr key={item.id}>
                          <td>{index + 1}</td>
                          <td>{item.category_name}</td>
                          <td>{item.stock}</td>
                          <td>{item.requestquantity}</td>
                          <td>{item.required_date?.split("T")[0]}</td>
                          <td>{item.vendor_name}</td>
                          <td>{item.approve_quantity}</td>
                          <td>{item.required_date?.split("T")[0]}</td>

                          {!isRequestStatus ? (
                            <>
                              <td>
                                {isPriceUpdateMode ? (
                                  // Price update mode - show received quantity as read-only
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    className="form-control form-control-sm"
                                    style={{ minWidth: "100px" }}
                                    value={item.recieved_quantity || ""}
                                    readOnly
                                  />
                                ) : (
                                  // Normal mode
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    className="form-control form-control-sm"
                                    style={{ minWidth: "100px" }}
                                    value={receivedQuantities[item.id] || ""}
                                    onChange={(e) => handleReceivedQuantityChange(item.id, e.target.value)}
                                    disabled={item.recieved_quantity && item.recieved_quantity !== "" && item.recieved_quantity !== "0"}
                                  />
                                )}
                              </td>
                              <td>
                                {/* Unit Price is always editable so users can fill or update
                                    the rate even after Received Qty has been submitted.
                                    Backend uses qty-delta, so re-submits don't double-add stock. */}
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  className="form-control form-control-sm"
                                  style={{ minWidth: "100px" }}
                                  value={receivedPrices[item.id] || ""}
                                  onChange={(e) => handleReceivedPriceChange(item.id, e.target.value)}
                                  disabled={parseFloat(item.price) > 0}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  style={{ minWidth: "120px" }}
                                  placeholder="Invoice No."
                                  value={receivedInvoices[item.id] || ""}
                                  onChange={(e) => handleReceivedInvoiceChange(item.id, e.target.value)}
                                />
                              </td>
                              <td>
                                <span className="fw-bold">
                                  ₹{(receivedTotals[item.id] || 0).toFixed(2)}
                                </span>
                              </td>
                              <td>
                                {item.recieved_quantity && item.recieved_quantity !== "" && item.recieved_quantity !== "0" ? (
                                  item.imageurl && item.imageurl !== "https://ims-backend.indevconsultancy.in/media/uploads/" ? (
                                    <img
                                      src={item.imageurl}
                                      alt="Captured"
                                      className="img-thumbnail"
                                      style={{ width: "80px", height: "60px", objectFit: "cover" }}
                                    />
                                  ) : (
                                    <span className="text-muted">No Image</span>
                                  )
                                ) : (
                                  renderWebcam(
                                    item.id,
                                    webcamRow === item.id,
                                    setWebcamRow,
                                    currentFacingMode,
                                    () => setCurrentFacingMode(prev => prev === "user" ? "environment" : "user"),
                                    () => captureImage(item.id),
                                    capturedImages[item.id],
                                    () => removeImage(item.id)
                                  )
                                )}
                              </td>
                            </>
                          ) : (
                            <td>
                              {item.required_date ? new Date(item.required_date).toLocaleDateString() : "-"}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                    {!isRequestStatus && (
                      <tfoot>
                        <tr className="table-success">
                          <td colSpan="11" className="text-end fw-bold">Grand Total:</td>
                          <td colSpan="2" className="text-start fw-bold">
                            ₹{grandTotal.toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
                onClick={() => {
                  setIsPriceUpdateMode(false);
                  setUpdatableItems([]);
                }}
              >
                Close
              </button>

              {/* Price Update Mode Button */}
              {isPriceUpdateMode && (
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={submitPriceUpdate}
                >
                  Update Prices
                </button>
              )}

              {/* Submit button — shown for Approved (1), Received (2) and Pending (3).
                  Backend uses qty-delta so re-submits only update price (and any
                  changed qty), without double-adding stock. */}
              {!isRequestStatus &&
                !isPriceUpdateMode && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={submitReceivedItems}
                  >
                    Submit Received Items
                  </button>
                )}
            </div>
          </div>
        </div>
      </div>

      {/* ========== PENDING DATA MODAL ========== */}
      <div className="modal fade" id="showPandingData" tabIndex="-1">
        <div className="modal-dialog modal-fullscreen">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Pending Quantity History</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" />
            </div>
            <div className="modal-body py-2">
              {getPandingDataShow.length === 0 ? (
                <p className="text-center text-danger">No pending data found</p>
              ) : (
                <div className="table-responsive">
                  <table className="table text-center table-bordered table-striped fs--1 mb-0">
                    <thead className="bg-primary text-white">
                      <tr>
                        <th>Item</th>
                        <th>Pending Quantity</th>
                        <th>Unit Price</th>
                        <th>Total Amount</th>
                        <th>Image</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPandingDataShow.map((item, index) => (
                        <tr key={item.id}>
                          <td>{item.itemname}</td>
                          <td>{item.pendingquantity}</td>
                          <td>₹{parseFloat(item.unit_price || item.price || 0).toFixed(2)}</td>
                          <td>₹{parseFloat(item.amount || 0).toFixed(2)}</td>
                          <td>
                            {item.imageurl && item.imageurl !== "" && item.imageurl !== "https://ims-backend.indevconsultancy.in/media/uploads/" ? (
                              <img
                                src={item.imageurl}
                                alt="Captured"
                                className="img-thumbnail"
                                style={{ width: "80px", height: "60px", objectFit: "cover" }}
                              />
                            ) : (
                              <span className="text-muted">No Image</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
          </div>
        </div>
      </div>

      {/* Add CSS for price update mode */}
      <style>
        {`
          .price-update-mode .form-control:not([readonly]) {
            border: 2px solid #ffc107;
            background-color: #fff9e6;
          }
          
          .price-update-mode .table {
            border: 2px solid #ffc107;
          }
          
          .price-update-mode .modal-header {
            background-color: #fff9e6;
          }
        `}
      </style>
    </div>
  );
};

export default Requisition;